/**
 * ModulesTab — lista modułów tenanta w ustawieniach (F7b T7b.8).
 *
 * Widok read-only: merge `catalog` + `subscriptions` z `/api/my/modules` (T7b.7).
 *  - Każdy moduł z catalog dostaje wiersz z nazwą, opisem, badge statusu:
 *    - active    → zielony „Aktywny"
 *    - trialing  → amber „Trial (do YYYY-MM-DD)"
 *    - nie w subs → szary „Nieaktywny"
 *  - Core moduły mają dodatkowy chip „Core".
 *
 * Akcje (upgrade/downgrade/cancel) są domeną super-admina (F7a T7.4) — tutaj
 * tylko informacja dla usera. CTA „Kontakt w celu aktywacji" dla inactive → F11+.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/Badge';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/Spinner';
import { type SubscribedModule, useMyModules } from '@/lib/use-my-modules';

function formatTrialEnd(iso: string | null, locale: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function ModulesTab() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useMyModules();

  const subsByCode = useMemo(() => {
    const map = new Map<string, SubscribedModule>();
    for (const s of data?.subscriptions ?? []) map.set(s.code, s);
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10" role="status">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
      >
        {t('tenant.modulesTab.error')}
      </div>
    );
  }

  const catalog = data?.catalog ?? [];
  if (catalog.length === 0) {
    return (
      <Card padding="md" data-testid="modules-tab-empty">
        <p className="text-sm text-text-tertiary">{t('tenant.modulesTab.empty')}</p>
      </Card>
    );
  }

  return (
    <ul className="space-y-3" data-testid="modules-tab-list">
      {catalog.map((mod) => {
        const sub = subsByCode.get(mod.code);
        const isSubscribed = !!sub;
        const badgeColor = !isSubscribed ? 'gray' : sub.status === 'active' ? 'green' : 'amber';
        const badgeLabel = !isSubscribed
          ? t('tenant.modulesTab.statusUnsubscribed')
          : sub.status === 'active'
            ? t('tenant.modulesTab.statusActive')
            : t('tenant.modulesTab.statusTrialing', {
                date: formatTrialEnd(sub.trialEndsAt, i18n.language),
              });
        return (
          <li key={mod.code}>
            <Card padding="md" data-testid={`modules-tab-${mod.code}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-text-primary">{mod.name}</span>
                    {mod.isCore && <Badge color="accent">{t('tenant.modulesTab.coreBadge')}</Badge>}
                  </div>
                  {mod.description && (
                    <p className="mt-1 text-xs text-text-tertiary">{mod.description}</p>
                  )}
                  {mod.category && (
                    <div className="mt-1 text-[10px] uppercase tracking-wide text-text-muted">
                      {mod.category}
                    </div>
                  )}
                </div>
                <div>
                  <Badge color={badgeColor}>{badgeLabel}</Badge>
                </div>
              </div>
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
